// src/services/networkManager.js
//
// A centralized service to manage network data fetching for the Team Management component
// This service handles caching, request deduplication, and proper error handling

import { getValidSignature } from './signatureService';
import { handleApiResponse } from '../utils/apiUtils';

// Cache for network data
const dataCache = {
  teamStats: new Map(),
  completedOrders: new Map(),
  teamPoints: new Map(),
  lastFetch: new Map(),
  expiryTime: 60000, // 1 minute cache
};

/**
 * Clear the data cache
 * @param {string} nodeId - Optional node ID to clear specific cache
 */
export const clearNetworkCache = (nodeId = null) => {
  if (nodeId) {
    dataCache.teamStats.delete(nodeId);
    dataCache.completedOrders.delete(nodeId);
    dataCache.teamPoints.delete(nodeId);
    dataCache.lastFetch.delete(`${nodeId}-stats`);
    dataCache.lastFetch.delete(`${nodeId}-orders`);
    dataCache.lastFetch.delete(`${nodeId}-points`);
  } else {
    dataCache.teamStats.clear();
    dataCache.completedOrders.clear();
    dataCache.teamPoints.clear();
    dataCache.lastFetch.clear();
  }
};

/**
 * Check if cached data is still valid
 * @param {string} cacheKey - The cache key to check
 * @returns {boolean} - Whether the cache is still valid
 */
const isCacheValid = (cacheKey) => {
  const lastFetch = dataCache.lastFetch.get(cacheKey);
  return lastFetch && (Date.now() - lastFetch < dataCache.expiryTime);
};

/**
 * Get team statistics with caching
 * Updated to use the team-stats endpoint as per API documentation
 * @param {Object} authData - Authentication data
 * @param {number} nodeId - Node ID
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Object>} - Team statistics data
 */
export const getTeamStats = async (authData, nodeId, forceRefresh = false) => {
  const cacheKey = `${nodeId}-stats`;
  
  // Return cached data if available and not forcing refresh
  if (!forceRefresh && isCacheValid(cacheKey) && dataCache.teamStats.has(nodeId)) {
    console.log('Using cached team stats for node', nodeId);
    return { success: true, data: dataCache.teamStats.get(nodeId) };
  }
  
  try {
    console.log('Fetching team stats for node', nodeId);
    
    // Get fresh authentication data if needed
    const auth = await ensureValidAuth(authData);
    
    // Ensure blockchain_network_id is a number
    let networkId = auth.blockchain_network_id;
    if (typeof networkId === 'string') {
      if (networkId === 'ethereum') networkId = 1;
      else if (networkId === 'bsc') networkId = 2;
      else if (networkId === 'solana') networkId = 3;
      else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
    }
    
    // Use the team-stats endpoint as per API documentation
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.aeronyx.network/api'}/nodes/team-stats/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...auth,
        blockchain_network_id: networkId,
        node_id: nodeId
      }),
    });
    
    const result = await handleApiResponse(response);
    
    if (result.success) {
      // Cache the result
      dataCache.teamStats.set(nodeId, result.data);
      dataCache.lastFetch.set(cacheKey, Date.now());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching team statistics:', error);
    throw error;
  }
}

/**
 * Get node completed orders with caching
 * @param {Object} authData - Authentication data
 * @param {number} nodeId - Node ID
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Object>} - Node completed orders data
 */
export const getNodeCompletedOrders = async (authData, nodeId, forceRefresh = false) => {
  const cacheKey = `${nodeId}-orders`;
  
  // Return cached data if available and not forcing refresh
  if (!forceRefresh && isCacheValid(cacheKey) && dataCache.completedOrders.has(nodeId)) {
    console.log('Using cached completed orders for node', nodeId);
    return { success: true, data: dataCache.completedOrders.get(nodeId) };
  }
  
  try {
    console.log('Fetching completed orders for node', nodeId);
    
    // Get fresh authentication data if needed
    const auth = await ensureValidAuth(authData);
    
    // Ensure blockchain_network_id is a number
    let networkId = auth.blockchain_network_id;
    if (typeof networkId === 'string') {
      if (networkId === 'ethereum') networkId = 1;
      else if (networkId === 'bsc') networkId = 2;
      else if (networkId === 'solana') networkId = 3;
      else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.aeronyx.network/api'}/nodes/completed-orders/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...auth,
        blockchain_network_id: networkId,
        node_id: nodeId
      }),
    });
    
    const result = await handleApiResponse(response);
    
    if (result.success) {
      // Cache the result
      dataCache.completedOrders.set(nodeId, result.data);
      dataCache.lastFetch.set(cacheKey, Date.now());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching completed orders:', error);
    throw error;
  }
};

/**
 * Get team points with caching
 * Updated to use the team-points endpoint as per API documentation
 * @param {Object} authData - Authentication data
 * @param {number} nodeId - Node ID
 * @param {string} period - Time period (7d, 30d, 90d, all)
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Object>} - Team points data
 */
export const getTeamPoints = async (authData, nodeId, period = 'all', forceRefresh = false) => {
  const cacheKey = `${nodeId}-points-${period}`;
  
  // Return cached data if available and not forcing refresh
  if (!forceRefresh && isCacheValid(cacheKey) && dataCache.teamPoints.has(cacheKey)) {
    console.log('Using cached team points for node', nodeId);
    return { success: true, data: dataCache.teamPoints.get(cacheKey) };
  }
  
  try {
    console.log('Fetching team points for node', nodeId, 'with period', period);
    
    // Get fresh authentication data if needed
    const auth = await ensureValidAuth(authData);
    
    // Ensure blockchain_network_id is a number
    let networkId = auth.blockchain_network_id;
    if (typeof networkId === 'string') {
      if (networkId === 'ethereum') networkId = 1;
      else if (networkId === 'bsc') networkId = 2;
      else if (networkId === 'solana') networkId = 3;
      else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
    }
    
    // Use the team-points endpoint as per API documentation
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.aeronyx.network/api'}/nodes/team-points/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...auth,
        blockchain_network_id: networkId,
        node_id: nodeId,
        period
      }),
    });
    
    const result = await handleApiResponse(response);
    
    if (result.success) {
      // Cache the result
      dataCache.teamPoints.set(cacheKey, result.data);
      dataCache.lastFetch.set(cacheKey, Date.now());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching team points:', error);
    throw error;
  }
}

/**
 * Get network stability fees with proper API endpoint
 * Updated to use the network-stability-fees endpoint as per API documentation
 * @param {Object} authData - Authentication data
 * @param {number} nodeId - Node ID
 * @param {Object} options - Options like page, pageSize, startDate, endDate
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Object>} - Network stability fees data
 */
export const getNetworkStabilityFees = async (authData, nodeId, options = {}, forceRefresh = false) => {
  const { page = 1, pageSize = 20, startDate, endDate } = options;
  const cacheKey = `${nodeId}-fees-${page}-${pageSize}${startDate ? `-${startDate}` : ''}${endDate ? `-${endDate}` : ''}`;
  
  // Return cached data if available and not forcing refresh
  if (!forceRefresh && isCacheValid(cacheKey) && dataCache.networkStabilityFees && dataCache.networkStabilityFees.has(cacheKey)) {
    console.log('Using cached network stability fees for node', nodeId);
    return { success: true, data: dataCache.networkStabilityFees.get(cacheKey) };
  }
  
  try {
    console.log('Fetching network stability fees for node', nodeId);
    
    // Get fresh authentication data if needed
    const auth = await ensureValidAuth(authData);
    
    // Ensure blockchain_network_id is a number
    let networkId = auth.blockchain_network_id;
    if (typeof networkId === 'string') {
      if (networkId === 'ethereum') networkId = 1;
      else if (networkId === 'bsc') networkId = 2;
      else if (networkId === 'solana') networkId = 3;
      else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
    }
    
    // Prepare request params
    const requestParams = {
      ...auth,
      blockchain_network_id: networkId,
      wallet_address: auth.wallet_address,
      page,
      page_size: pageSize
    };
    
    // Add optional date filters if provided
    if (startDate) requestParams.start_date = startDate;
    if (endDate) requestParams.end_date = endDate;
    
    // Use the correct API endpoint for network stability fees
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.aeronyx.network/api'}/points/network-stability-fees/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestParams),
    });
    
    const result = await handleApiResponse(response);
    
    if (result.success) {
      // Initialize networkStabilityFees cache if it doesn't exist
      if (!dataCache.networkStabilityFees) {
        dataCache.networkStabilityFees = new Map();
      }
      
      // Cache the result
      dataCache.networkStabilityFees.set(cacheKey, result.data);
      dataCache.lastFetch.set(cacheKey, Date.now());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching network stability fees:', error);
    throw error;
  }
}

/**
 * Ensure we have valid authentication data
 * @param {Object} authData - Current authentication data
 * @returns {Promise<Object>} - Valid authentication data
 */
async function ensureValidAuth(authData) {
  if (!authData || !authData.wallet_address) {
    throw new Error('Authentication data is required');
  }
  
  try {
    // Handle blockchain_network_id conversion
    let networkId = authData.blockchain_network_id || 1;
    if (typeof networkId === 'string') {
      if (networkId === 'ethereum') networkId = 1;
      else if (networkId === 'bsc') networkId = 2;
      else if (networkId === 'solana') networkId = 3;
      else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
    }
    
    // Get a valid signature (will use cached if available)
    const { signature, message } = await getValidSignature(
      authData.wallet_address,
      networkId,
      authData.wallet_type,
      false // Don't force refresh unless needed
    );
    
    return {
      wallet_address: authData.wallet_address,
      blockchain_network_id: networkId,
      signature,
      message,
      wallet_type: authData.wallet_type
    };
  } catch (error) {
    console.error('Error getting valid authentication:', error);
    throw new Error('Failed to authenticate wallet: ' + error.message);
  }
}

/**
 * Generate network visualization data from team statistics
 * @param {Object} data - Team statistics data
 * @returns {Object} - Network visualization data
 */
export const generateNetworkVisualizationData = (data) => {
  if (!data) {
    return { nodes: [], links: [] };
  }
  
  // Initialize with root node
  const nodes = [
    // Root node (selected node)
    {
      id: 'root',
      label: data.node?.reference_code || 'Your Node',
      type: 'root',
      tier: data.node?.tier || 'Standard',
      level: 0
    }
  ];
  
  const links = [];
  
  // Handle team_statistics structure (from team-stats endpoint)
  if (data.team_statistics) {
    // Add direct nodes (level 1)
    if (data.direct_nodes && Array.isArray(data.direct_nodes)) {
      data.direct_nodes.forEach((node) => {
        nodes.push({
          id: `node-${node.id}`,
          label: node.reference_code,
          type: 'subordinate',
          tier: node.tier,
          level: node.relationship_level || 1
        });
        
        // Connect to root
        links.push({
          source: 'root',
          target: `node-${node.id}`,
          level: 1
        });
      });
    }
    
    // Add indirect nodes (level 2)
    if (data.indirect_nodes && Array.isArray(data.indirect_nodes)) {
      data.indirect_nodes.forEach((node) => {
        nodes.push({
          id: `node-${node.id}`,
          label: node.reference_code,
          type: 'subordinate',
          tier: node.tier,
          level: node.relationship_level || 2
        });
        
        // For indirect nodes, try to find a direct parent
        if (node.parent_node_id) {
          links.push({
            source: `node-${node.parent_node_id}`,
            target: `node-${node.id}`,
            level: 2
          });
        } else {
          // If parent info not available, connect to root as fallback
          links.push({
            source: 'root',
            target: `node-${node.id}`,
            level: 2
          });
        }
      });
    }
  } else if (data.network_statistics) {
    // Handle node-downline endpoint structure
    // Add subordinate nodes from the subordinates array
    if (data.subordinates && Array.isArray(data.subordinates)) {
      data.subordinates.forEach((node) => {
        nodes.push({
          id: `node-${node.id}`,
          label: node.reference_code,
          type: 'subordinate',
          tier: node.tier_name || node.tier,
          level: node.relationship_level || 1
        });
        
        // Connect to root for simplicity unless we have parent info
        if (node.parent_node_id) {
          links.push({
            source: `node-${node.parent_node_id}`,
            target: `node-${node.id}`,
            level: node.relationship_level || 1
          });
        } else {
          links.push({
            source: 'root',
            target: `node-${node.id}`,
            level: node.relationship_level || 1
          });
        }
      });
    }
  }
  
  return { nodes, links };
}
